import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { KemCardStyleKeyType } from '@/constants/kemCardStylesList'
import { CardIssueState, KemCardProvider } from '@/domain/wallet/walletTypes'
import { ApiErrorCode } from '@/api/apiTypes'
import { useAppHydrationStore } from './useAppHydrationStore'

export type KemCardScreenType =
  | 'WITH_CARD'
  | 'WITHOUT_CARD'
  | 'PROCESSING'
  | 'REJECTED'
  | 'ERROR'
  | 'LOADING'

export interface KemCardScreenState {
  screenType: KemCardScreenType
  kemCardId?: string
  cardData?: {
    id: string
    first_six: string
    last_four: string
    state: string
    network: string
    card_type: string
    card_provider?: KemCardProvider
  }
  issueState?: CardIssueState
  errorCode?: ApiErrorCode | string
  isLoading: boolean
  lastUpdated?: number
}

export interface SetPinMfaPayload {
  encrypted_pin: {
    iv: string
    data: string
  }
  card_id: string
  session_id: string
}

interface KemCardStore {
  kemCardStyleKey: KemCardStyleKeyType
  setKemCardStyleKey: (styleKey: KemCardStyleKeyType) => void

  // Screen state management
  screenState: KemCardScreenState
  setScreenState: (state: KemCardScreenState) => void
  resetScreenState: () => void

  // PIN MFA payload
  setPinMfaPayload: SetPinMfaPayload | null
  setSetPinMfaPayload: (payload: SetPinMfaPayload) => void
  resetSetPinMfaPayload: () => void

  // Hydration tracking
  _hasHydrated: boolean
  setHasHydrated: (state: boolean) => void
}

const defaultScreenState: KemCardScreenState = {
  screenType: 'LOADING',
  isLoading: true,
}

/**
 * Synchronously read the initial screen state from localStorage.
 * This ensures we have the correct state on the very first render,
 * before zustand's async hydration completes.
 */
const getInitialScreenState = (): KemCardScreenState => {
  try {
    const stored = localStorage.getItem('kem-card-store')
    if (stored) {
      const parsed = JSON.parse(stored)
      if (parsed?.state?.screenState) {
        return parsed.state.screenState
      }
    }
  } catch {
    // Ignore parse errors, fall back to default
  }
  return defaultScreenState
}

// Create the store with persist middleware
// Use synchronously-read initial state to prevent flash
const initialScreenState = getInitialScreenState()

const useKemCardStoreBase = create<KemCardStore>()(
  persist(
    (set) => ({
      kemCardStyleKey: 'singularity',
      setKemCardStyleKey: (styleKey: KemCardStyleKeyType) =>
        set({ kemCardStyleKey: styleKey }),

      // Use the synchronously-read initial state instead of default
      screenState: initialScreenState,
      setScreenState: (state: KemCardScreenState) =>
        set({
          screenState: {
            ...state,
            lastUpdated: Date.now(),
          },
        }),
      resetScreenState: () => set({ screenState: defaultScreenState }),

      // PIN MFA payload
      setPinMfaPayload: null,
      setSetPinMfaPayload: (payload: SetPinMfaPayload) =>
        set({ setPinMfaPayload: payload }),
      resetSetPinMfaPayload: () => set({ setPinMfaPayload: null }),

      _hasHydrated: false,
      setHasHydrated: (state: boolean) => set({ _hasHydrated: state }),
    }),
    {
      name: 'kem-card-store',
      // Persist both style preference and screen state for instant hydration
      partialize: (state) => ({
        kemCardStyleKey: state.kemCardStyleKey,
        screenState: state.screenState,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
        // Signal to centralized hydration store
        useAppHydrationStore.getState().setKemCardStoreHydrated()
      },
    },
  ),
)

// Export the store
// Note: Initial screenState is synchronously read from localStorage in getInitialScreenState(),
// so the first render already has the correct state - no hydration waiting needed
export const useKemCardStore = useKemCardStoreBase
